import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Wine, Cigarette, Coffee, Smartphone, ShoppingCart, Gamepad2, Utensils, Activity, Sun, Droplets, Pill, Heart, Sparkles, Settings, Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import WhyThisMattersCard from './education/WhyThisMattersCard';

interface HabitPreference {
  id: string;
  name: string;
  icon: React.ReactNode;
  category: 'nourish' | 'release';
  description: string;
  whyItMatters: string;
  researchSummary: string;
  howToOptimise?: string;
  tags: string[];
}

const HABIT_OPTIONS: HabitPreference[] = [
  // Nourish & Grow
  {
    id: 'exercise',
    name: 'Exercise & Movement',
    icon: <Activity className="w-5 h-5" />,
    category: 'nourish',
    description: 'Track your physical activity, movement patterns, and exercise commitment',
    whyItMatters: 'Regular exercise is one of the most powerful tools for mental health, reducing symptoms of depression and anxiety by 30-40% while improving cognitive function, sleep quality, and emotional regulation.',
    researchSummary: 'Meta-analyses show exercise is as effective as medication for mild-to-moderate depression. It increases BDNF (brain-derived neurotrophic factor), promotes neurogenesis, and regulates stress hormones like cortisol.',
    howToOptimise: 'Aim for 150 minutes of moderate activity weekly. Morning movement optimises circadian rhythms. Even 10-minute bursts throughout the day provide benefits.',
    tags: ['mental health', 'physical health', 'mood regulation']
  },
  {
    id: 'sleep',
    name: 'Sleep Quality',
    icon: <Sparkles className="w-5 h-5" />,
    category: 'nourish',
    description: 'Monitor sleep patterns, quality, and environmental factors',
    whyItMatters: 'Sleep is essential for emotional regulation, memory consolidation, and mental health. Poor sleep increases depression risk by 300% and anxiety by 200%.',
    researchSummary: 'During sleep, your brain clears toxic proteins, consolidates memories, and regulates emotions. REM sleep processes emotional experiences, while deep sleep restores physical health.',
    howToOptimise: 'Maintain consistent sleep/wake times. Create a dark, cool environment (60-67°F). Avoid screens 1-2 hours before bed. Morning sunlight helps regulate your circadian rhythm.',
    tags: ['mental health', 'recovery', 'cognitive function']
  },
  {
    id: 'nutrition',
    name: 'Food & Nutrition',
    icon: <Utensils className="w-5 h-5" />,
    category: 'nourish',
    description: 'Track eating patterns, food quality, and relationship with food',
    whyItMatters: 'Your gut produces 90% of serotonin. Nutrition directly impacts mood, energy, and mental health. Processed foods increase inflammation and depression risk by 40%.',
    researchSummary: 'The gut-brain axis connects digestive health to mental wellbeing. Omega-3s, B vitamins, and fermented foods support neurotransmitter production. Blood sugar stability prevents mood crashes.',
    howToOptimise: 'Focus on whole foods, omega-3 rich fish, leafy greens, and fermented foods. Eat protein with each meal to stabilise blood sugar. Stay hydrated.',
    tags: ['nutrition', 'gut health', 'mental health']
  },
  {
    id: 'hydration',
    name: 'Water & Hydration',
    icon: <Droplets className="w-5 h-5" />,
    category: 'nourish',
    description: 'Track daily water intake and hydration habits',
    whyItMatters: 'Even mild dehydration (1-2%) impairs mood, concentration, and energy. Your brain is 75% water - proper hydration is essential for cognitive function.',
    researchSummary: 'Studies show dehydration increases cortisol, reduces cognitive performance, and worsens mood. Water supports neurotransmitter production and nutrient delivery to brain cells.',
    howToOptimise: 'Aim for 8-10 glasses daily. Drink a glass upon waking. Sip throughout the day rather than large amounts at once. Herbal teas count toward hydration.',
    tags: ['hydration', 'energy', 'cognitive function']
  },
  {
    id: 'sunlight',
    name: 'Sunlight & Nature',
    icon: <Sun className="w-5 h-5" />,
    category: 'nourish',
    description: 'Log morning sunlight exposure and time in nature',
    whyItMatters: 'Morning sunlight regulates circadian rhythms, boosts mood, and improves sleep quality. Just 20 minutes in nature reduces cortisol by 21%.',
    researchSummary: 'Sunlight triggers vitamin D production and regulates melatonin/serotonin balance. Nature exposure activates the parasympathetic nervous system, reducing stress hormones.',
    howToOptimise: 'Get 10-30 minutes of morning sunlight within 2 hours of waking. Spend 20+ minutes in nature daily. Walk barefoot on grass when possible for grounding.',
    tags: ['circadian rhythm', 'mood', 'stress reduction']
  },
  {
    id: 'medication',
    name: 'Medication Tracking',
    icon: <Pill className="w-5 h-5" />,
    category: 'nourish',
    description: 'Track prescribed medications and supplements',
    whyItMatters: 'Medication adherence is crucial for mental health management. Missed doses can trigger relapses, mood instability, and increased symptoms.',
    researchSummary: 'Consistent medication timing optimises therapeutic levels. Tracking helps identify patterns between adherence and symptom changes, supporting treatment effectiveness.',
    howToOptimise: 'Set daily reminders. Take medications at the same time each day. Never stop abruptly - work with your healthcare provider for any changes.',
    tags: ['treatment', 'adherence', 'mental health']
  },
  {
    id: 'social',
    name: 'Social Connection',
    icon: <Users className="w-5 h-5" />,
    category: 'nourish',
    description: 'Track meaningful social interactions and connection quality',
    whyItMatters: 'Social isolation is as harmful as smoking 15 cigarettes daily. Strong social connections reduce depression risk by 50% and boost longevity.',
    researchSummary: 'Loneliness increases inflammation, stress hormones, and mortality risk. Quality social connections activate reward centres, release oxytocin, and support emotional regulation.',
    howToOptimise: 'Aim for 2+ meaningful interactions daily. Prioritise in-person connections. Join communities aligned with your values. Practise active listening and vulnerability.',
    tags: ['connection', 'mental health', 'loneliness prevention']
  },

  // Support & Change
  {
    id: 'alcohol',
    name: 'Alcohol Awareness',
    icon: <Wine className="w-5 h-5" />,
    category: 'release',
    description: 'Track alcohol consumption, patterns, and reduction goals',
    whyItMatters: 'Alcohol disrupts sleep architecture, depletes serotonin, and worsens anxiety/depression. Even moderate drinking impacts mental health within 48-72 hours.',
    researchSummary: 'Alcohol is a depressant that disrupts REM sleep, reduces neuroplasticity, and creates a rebound anxiety effect. Regular use can worsen depression by 50%.',
    howToOptimise: 'Track units honestly. Identify triggers and alternative coping strategies. Take 2-3 alcohol-free days weekly. Seek support if struggling to reduce.',
    tags: ['harm reduction', 'mental health', 'sleep quality']
  },
  {
    id: 'smoking',
    name: 'Smoking & Nicotine',
    icon: <Cigarette className="w-5 h-5" />,
    category: 'release',
    description: 'Monitor smoking patterns and work toward reduction',
    whyItMatters: 'While nicotine temporarily reduces stress, it increases anxiety between cigarettes. Smoking is linked to 70% higher depression rates and reduces treatment effectiveness.',
    researchSummary: 'Nicotine creates a cycle of withdrawal-relief-withdrawal that worsens baseline anxiety. Quitting improves mood, reduces anxiety, and enhances medication effectiveness.',
    howToOptimise: 'Track cravings and triggers. Use delay tactics (wait 10 minutes). Replace hand-to-mouth habits. Consider nicotine replacement therapy with medical guidance.',
    tags: ['addiction', 'anxiety', 'harm reduction']
  },
  {
    id: 'caffeine',
    name: 'Caffeine Management',
    icon: <Coffee className="w-5 h-5" />,
    category: 'release',
    description: 'Track caffeine intake and its effects on sleep and anxiety',
    whyItMatters: 'Caffeine has a 5-7 hour half-life. Afternoon consumption disrupts sleep. Overconsumption increases anxiety, irritability, and can trigger panic attacks.',
    researchSummary: 'Caffeine blocks adenosine receptors, preventing natural tiredness signals. This creates sleep debt, mood instability, and increased stress hormone production.',
    howToOptimise: 'Limit to 1-2 cups before noon. Track anxiety levels 30-60 minutes after consumption. Consider gradual reduction if experiencing sleep issues or anxiety.',
    tags: ['sleep', 'anxiety', 'energy management']
  },
  {
    id: 'screen_time',
    name: 'Screen Time Balance',
    icon: <Smartphone className="w-5 h-5" />,
    category: 'release',
    description: 'Monitor digital device usage and social media patterns',
    whyItMatters: 'Excessive screen time, especially before bed, disrupts circadian rhythms. Social media use is linked to 40% increased depression rates and comparison anxiety.',
    researchSummary: 'Blue light suppresses melatonin for 3+ hours. Doomscrolling activates stress response. Social comparison activates the same brain regions as physical pain.',
    howToOptimise: 'No screens 1-2 hours before bed. Set app time limits. Replace scrolling with reading, movement, or connection. Use blue light filters after sunset.',
    tags: ['digital wellness', 'sleep', 'mental health']
  },
  {
    id: 'shopping',
    name: 'Spending & Shopping',
    icon: <ShoppingCart className="w-5 h-5" />,
    category: 'release',
    description: 'Track impulse purchases and emotional spending patterns',
    whyItMatters: 'Emotional spending provides temporary dopamine but often leads to guilt, financial stress, and shame cycles. Shopping addiction affects 6% of the population.',
    researchSummary: 'Retail therapy activates reward circuits but creates boom-bust dopamine cycles. Financial stress significantly worsens anxiety and depression.',
    howToOptimise: 'Implement 24-hour waiting periods. Track emotional states before purchases. Calculate true cost (hours worked). Find healthier dopamine sources.',
    tags: ['impulse control', 'financial wellness', 'coping mechanisms']
  },
  {
    id: 'gaming',
    name: 'Gaming Balance',
    icon: <Gamepad2 className="w-5 h-5" />,
    category: 'release',
    description: 'Monitor gaming time and its impact on daily functioning',
    whyItMatters: 'While gaming can be relaxing, excessive use disrupts sleep, reduces real-world connection, and can worsen depression. Gaming disorder affects 3-4% of players.',
    researchSummary: 'Gaming activates powerful reward circuits. Late-night gaming disrupts circadian rhythms. Excessive use correlates with social isolation and mood disorders.',
    howToOptimise: 'Set time limits (1-2 hours daily). Avoid gaming within 2 hours of bedtime. Balance with physical activity and social connection. Notice if gaming is avoidance.',
    tags: ['digital wellness', 'balance', 'social connection']
  }
];

interface HabitPreferencesSelectorProps {
  onSave?: () => void;
  onClose?: () => void;
  compact?: boolean;
}

export default function HabitPreferencesSelector({ onSave, onClose, compact = false }: HabitPreferencesSelectorProps) {
  const [selectedHabits, setSelectedHabits] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('daily_checkins')
        .select('habit_preferences')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data?.habit_preferences) {
        setSelectedHabits(new Set(data.habit_preferences));
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to save your preferences.",
          variant: "destructive"
        });
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      const habitPreferences = Array.from(selectedHabits);

      const { error } = await supabase
        .from('daily_checkins')
        .upsert({
          user_id: user.id,
          date: today,
          habit_preferences: habitPreferences,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,date'
        });

      if (error) throw error;

      toast({
        title: "Preferences saved!",
        description: `You've selected ${habitPreferences.length} habit${habitPreferences.length !== 1 ? 's' : ''} to track.`
      });

      if (onSave) onSave();
    } catch (error: any) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Could not save preferences",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleHabit = (habitId: string) => {
    setSelectedHabits(prev => {
      const newSet = new Set(prev);
      if (newSet.has(habitId)) {
        newSet.delete(habitId);
      } else {
        newSet.add(habitId);
      }
      return newSet;
    });
  };

  const nourishHabits = HABIT_OPTIONS.filter(h => h.category === 'nourish');
  const releaseHabits = HABIT_OPTIONS.filter(h => h.category === 'release');

  // Generate unique gradient for each card based on index
  const getCardGradient = (index: number, category: 'nourish' | 'release') => {
    const gradients = [
      'from-primary/5 via-transparent to-accent/5',
      'from-accent/8 via-transparent to-primary/3',
      'from-primary/3 via-accent/5 to-transparent',
      'from-transparent via-primary/5 to-accent/8',
      'from-accent/5 via-primary/3 to-transparent',
      'from-primary/8 via-transparent to-accent/3',
      'from-transparent via-accent/8 to-primary/5',
    ];
    return gradients[index % gradients.length];
  };

  const renderHabitCard = (habit: HabitPreference, index: number) => (
    <Card 
      key={habit.id} 
      className={`p-4 hover:shadow-lg transition-all duration-300 bg-gradient-to-br ${getCardGradient(index, habit.category)} border-primary/10 hover:border-primary/30`}
    >
      <div className="flex items-start gap-4">
        <Checkbox
          id={habit.id}
          checked={selectedHabits.has(habit.id)}
          onCheckedChange={() => toggleHabit(habit.id)}
          className="mt-1"
        />
        <div className="flex-1">
          <Label htmlFor={habit.id} className="flex items-center gap-2 text-base font-semibold cursor-pointer mb-2">
            <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
              {habit.icon}
            </span>
            {habit.name}
          </Label>
          <p className="text-sm text-muted-foreground mb-3">{habit.description}</p>
          
          {!compact && (
            <WhyThisMattersCard
              toolName={habit.name}
              shortDescription={habit.description}
              whyItMatters={habit.whyItMatters}
              researchSummary={habit.researchSummary}
              howToOptimise={habit.howToOptimise}
              tags={habit.tags}
            />
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
          <Heart className="w-6 h-6 text-primary" />
          Choose Your Growth Journey
        </h2>
        <p className="text-muted-foreground">
          Select the habits you'd like to track and develop. You can always change these later.
        </p>
      </div>

      <Tabs defaultValue="nourish" className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-auto p-1">
          <TabsTrigger value="nourish" className="flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs sm:text-sm whitespace-nowrap">
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Nourish & Grow</span>
            <span className="flex-shrink-0 text-[10px] sm:text-xs bg-primary/20 px-1.5 py-0.5 rounded-full">
              {nourishHabits.filter(h => selectedHabits.has(h.id)).length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="release" className="flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs sm:text-sm whitespace-nowrap">
            <Heart className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Support & Change</span>
            <span className="flex-shrink-0 text-[10px] sm:text-xs bg-primary/20 px-1.5 py-0.5 rounded-full">
              {releaseHabits.filter(h => selectedHabits.has(h.id)).length}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="nourish" className="space-y-4 mt-6">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Building Healthy Habits
            </h3>
            <p className="text-sm text-muted-foreground">
              These habits nourish your wellbeing and support your growth. Each is backed by research showing significant mental and physical health benefits.
            </p>
          </div>
          {nourishHabits.map((habit, index) => renderHabitCard(habit, index))}
        </TabsContent>

        <TabsContent value="release" className="space-y-4 mt-6">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              Compassionate Change
            </h3>
            <p className="text-sm text-muted-foreground">
              These trackers help you understand patterns and work toward change with self-compassion. Progress isn't linear - every insight counts.
            </p>
          </div>
          {releaseHabits.map((habit, index) => renderHabitCard(habit, index))}
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-between pt-6 border-t">
        <div className="text-sm text-muted-foreground">
          {selectedHabits.size} habit{selectedHabits.size !== 1 ? 's' : ''} selected
        </div>
        <div className="flex gap-3">
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button 
            onClick={savePreferences} 
            disabled={isLoading || selectedHabits.size === 0}
            className="flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            {isLoading ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </div>

      <div className="text-center text-sm text-muted-foreground">
        You can update these preferences anytime from your settings
      </div>
    </div>
  );
}
